import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Pool } from "../lib/api";

const NEW = "new";

/** "3 people", "1 person". */
export const people = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

/**
 * Puts one or more candidates into a talent pool: an existing one, or a new one named here. Used
 * on search results (everyone listed) and on a candidate's page.
 */
export function AddToPool({
  candidateIds,
  exclude = [],
  onCancel,
  onAdded,
}: {
  candidateIds: string[];
  /** Pools the candidate is already in, left out of the list. */
  exclude?: string[];
  onCancel: () => void;
  onAdded: (pool: { id: string; name: string }, message: string) => void;
}) {
  const [pools, setPools] = useState<Pool[] | null>(null);
  const [choice, setChoice] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const load = useAction();
  const save = useAction();

  useEffect(() => {
    void load.run(async () => {
      const list = (await api.pools()).pools.filter((p) => !exclude.includes(p.id));
      setPools(list);
      setChoice(list[0]?.id ?? NEW);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => {
      let pool = pools!.find((p) => p.id === choice);
      if (!pool) {
        pool = (await api.createPool(name.trim())).pool;
        // If adding fails, trying again uses the pool just made rather than making it twice.
        setPools([...pools!, pool]);
        setChoice(pool.id);
      }
      const r = await api.addToPool(pool.id, candidateIds, note.trim());
      const extra = [r.alreadyIn > 0 && `${people(r.alreadyIn)} already in it`, r.skipped > 0 && `${people(r.skipped)} couldn't be added`].filter(Boolean);
      onAdded(pool, `Added ${people(r.added)} to ${pool.name}${extra.length ? ` (${extra.join(", ")})` : ""}.`);
    });
  };

  const many = candidateIds.length > 1;
  return (
    <form className="form panel" onSubmit={submit} aria-label="Add to a talent pool">
      <ErrorNotice error={load.error} />
      {pools && (
        <div className="grid-2">
          <label className="field">
            <span className="label">Pool</span>
            <select value={choice} onChange={(e) => setChoice(e.target.value)}>
              {pools.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.size})
                </option>
              ))}
              <option value={NEW}>New pool…</option>
            </select>
          </label>
          {choice === NEW && (
            <label className="field">
              <span className="label">New pool's name</span>
              <input value={name} maxLength={100} required autoFocus placeholder="e.g. Leeds Go engineers" onChange={(e) => setName(e.target.value)} />
            </label>
          )}
        </div>
      )}
      <label className="field">
        <span className="label">Why they're in it (optional)</span>
        <input value={note} maxLength={500} placeholder={many ? "Saved with each person" : "e.g. Keen on hybrid, wants 80k+"} onChange={(e) => setNote(e.target.value)} />
      </label>
      <ErrorNotice error={save.error} />
      <div className="button-row">
        <button className="btn" disabled={!pools || save.pending || (choice === NEW && !name.trim())}>
          {save.pending ? "Adding…" : many ? `Add ${people(candidateIds.length)}` : "Add to pool"}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The message after adding, with a link to the pool. */
export function AddedNotice({ added, onDismiss }: { added: { pool: { id: string; name: string }; message: string } | null; onDismiss?: () => void }) {
  if (!added) return null;
  return (
    <p className="notice ok" role="status">
      {added.message} <Link to={`/pools/${added.pool.id}`}>Open the pool</Link>
      {onDismiss && (
        <>
          {" "}
          <button type="button" className="link-btn" onClick={onDismiss}>
            Dismiss
          </button>
        </>
      )}
    </p>
  );
}
